import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DlirSubmissionPackCard = ({ pack, onUpdateStatus, onDownload }) => {
  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Pending', icon: 'Clock' },
      prepared: { bg: 'bg-primary/10', text: 'text-primary', label: 'Prepared', icon: 'Package' },
      submitted: { bg: 'bg-warning/10', text: 'text-warning', label: 'Submitted', icon: 'Send' },
      acknowledged: { bg: 'bg-success/10', text: 'text-success', label: 'Acknowledged', icon: 'CheckCheck' },
      rejected: { bg: 'bg-error/10', text: 'text-error', label: 'Rejected', icon: 'XCircle' }
    };
    return badges?.[status] || badges?.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const badge = getStatusBadge(pack?.submissionStatus);

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Package" size={20} color="var(--color-primary)" />
            <h4 className="font-heading font-semibold text-base text-foreground">
              {pack?.packName}
            </h4>
          </div>
          <p className="caption text-xs text-muted-foreground mb-2">
            Period: {pack?.submissionPeriod}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Icon name={badge?.icon} size={16} color={badge?.text?.replace('text-', 'var(--color-)')} />
          <span className={`px-2 py-1 ${badge?.bg} ${badge?.text} text-xs font-caption rounded-full`}>
            {badge?.label}
          </span>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {pack?.dlirReferenceNumber && (
          <div className="flex items-center gap-2">
            <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
            <p className="caption text-xs text-muted-foreground">
              DLIR Reference: <span className="font-medium text-foreground">{pack?.dlirReferenceNumber}</span>
            </p>
          </div>
        )}
        {pack?.acknowledgementReference && (
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={14} color="var(--color-success)" />
            <p className="caption text-xs text-muted-foreground">
              Acknowledgement: <span className="font-medium text-foreground">{pack?.acknowledgementReference}</span>
            </p>
          </div>
        )}
        {pack?.preparedBy && (
          <div className="flex items-center gap-2">
            <Icon name="User" size={14} color="var(--color-muted-foreground)" />
            <p className="caption text-xs text-muted-foreground">
              Prepared by {pack?.preparedBy} on {formatDate(pack?.preparedAt)}
            </p>
          </div>
        )}
        {pack?.submittedToDlirAt && (
          <div className="flex items-center gap-2">
            <Icon name="Send" size={14} color="var(--color-primary)" />
            <p className="caption text-xs text-muted-foreground">
              Submitted: {formatDate(pack?.submittedToDlirAt)}
            </p>
          </div>
        )}
        {pack?.acknowledgedByDlirAt && (
          <div className="flex items-center gap-2">
            <Icon name="CheckCheck" size={14} color="var(--color-success)" />
            <p className="caption text-xs text-muted-foreground">
              Acknowledged: {formatDate(pack?.acknowledgedByDlirAt)}
            </p>
          </div>
        )}
        {pack?.rejectionReason && (
          <div className="flex items-start gap-2 p-2 bg-error/10 rounded">
            <Icon name="AlertCircle" size={14} color="var(--color-error)" className="mt-0.5" />
            <p className="caption text-xs text-error">
              Rejection Reason: {pack?.rejectionReason}
            </p>
          </div>
        )}
      </div>

      {pack?.metadata && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 p-3 bg-muted rounded">
          {pack?.metadata?.total_employees && (
            <div>
              <p className="caption text-xs text-muted-foreground">Employees</p>
              <p className="font-heading font-semibold text-sm text-foreground">{pack?.metadata?.total_employees}</p>
            </div>
          )}
          {pack?.metadata?.total_payroll && (
            <div>
              <p className="caption text-xs text-muted-foreground">Total Payroll</p>
              <p className="font-heading font-semibold text-sm text-foreground">K{pack?.metadata?.total_payroll?.toLocaleString()}</p>
            </div>
          )}
          {pack?.metadata?.filing_confirmations && (
            <div>
              <p className="caption text-xs text-muted-foreground">Filings</p>
              <p className="font-heading font-semibold text-sm text-foreground">{pack?.metadata?.filing_confirmations}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {pack?.completePackUrl && (
          <Button
            variant="default"
            size="sm"
            onClick={() => onDownload?.(pack?.id)}
            iconName="Download"
            iconPosition="left"
          >
            Download Pack
          </Button>
        )}
        {pack?.submissionStatus === 'prepared' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUpdateStatus?.(pack?.id, 'submitted')}
            iconName="Send"
            iconPosition="left"
          >
            Submit to DLIR
          </Button>
        )}
      </div>
    </div>
  );
};

export default DlirSubmissionPackCard;